/*
Capabilities:
- CAP-MEMBER-FAMILY-MANAGEMENT

Responsibilities:
- Render and validate the child-registration modal flow for linked members.

Context:
- project-intelligence/capabilities/requested/member/family-management.md
*/

import React, { useState, useRef } from 'react';
import { isMobile } from 'react-device-detect';

import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import objectHash from 'object-hash';

import { HorizontallyCenteringContainer, VerticalSeparator, registerChild } from './utils';
import { FullWidthSpinner } from '../loader-components';

const MIN_AGE = 4;
const MAX_AGE = 13;

export default function RegisterKid({ token, closeModal, updateMemberData }) {
    /*
    CAP-MEMBER-FAMILY-MANAGEMENT entry point for child registration.

    Notable frontend behavior:
    - enforces the 4-to-13 age window locally,
    - submits the parent token rather than a child-specific token,
    - uses local storage to smooth over ambiguous repeat-success cases.
    */
    const [isSigningUp, setIsSigningUp] = useState(false);

    const nameEl = useRef(null);
    const emailEl = useRef(null);
    const dateOfBirthEl = useRef(null);
    // const allowSMSCheckbox = useRef(null);
    // const allowEmailCheckbox = useRef(null);
    // const allowInstantMessengerServicesCheckbox = useRef(null);
    const cfmInfoRealCheckbox = useRef(null);
    const cfmAcceptTnCCheckbox = useRef(null);

    const [overallErrorText, setOverallErrorText] = useState(null);

    const [nameErrorText, setNameErrorText] = useState(null);
    const [emailErrorTextLine1, setEmailErrorTextLine1] = useState(null);
    const [emailErrorTextLine2, setEmailErrorTextLine2] = useState(null);
    const [dateOfBirthErrorText, setDateOfBirthErrorText] = useState(null);
    const [cfmInfoRealErrorText, setCfmInfoRealErrorText] = useState(null);
    const [cfmAcceptTnCErrorText, setCfmAcceptTnCErrorText] = useState(null);

    const dataElements = {
        name: { el: nameEl, getValue: () => nameEl?.current?.value && nameEl?.current?.value.trim(), setError: setNameErrorText },
        email: { el: emailEl, getValue: () => emailEl?.current?.value && emailEl?.current?.value.trim(), setError: (l1, l2) => { setEmailErrorTextLine1(l1); setEmailErrorTextLine2(l2); } },
        dateOfBirth: { el: dateOfBirthEl, getValue: () => dateOfBirthEl?.current?.value && dateOfBirthEl?.current?.value.trim(), setError: setDateOfBirthErrorText },
        // allowSMS: { el: allowSMSCheckbox, getValue: () => allowSMSCheckbox?.current?.checked, setError: null },
        // allowEmail: { el: allowEmailCheckbox, getValue: () => allowEmailCheckbox?.current?.checked, setError: null },
        // allowInstantMessengerServices: { el: allowInstantMessengerServicesCheckbox, getValue: () => allowInstantMessengerServicesCheckbox?.current?.checked, setError: null },
        cfmInfoReal: { el: cfmInfoRealCheckbox, getValue: () => cfmInfoRealCheckbox?.current?.checked, setError: setCfmInfoRealErrorText },
        cfmAcceptTnC: { el: cfmAcceptTnCCheckbox, getValue: () => cfmAcceptTnCCheckbox?.current?.checked, setError: setCfmAcceptTnCErrorText },
    }

    function getData({ validateItem = null } = {}) {
        const data = Object.fromEntries(
            Object.keys(dataElements).map(k => {
                return [k, dataElements[k].getValue()]
            })
        );
        data.token = token;
        let numErrors = 0;

        if (validateItem === 'name' || validateItem === 'all') {
            if(!data.name.trim()) {
                dataElements.name.setError('Please enter your name.')
                numErrors++;
            } else {
                dataElements.name.setError(null);
            }
        }

        if (validateItem === 'email' || validateItem === 'all') {
            // eslint-disable-next-line no-control-regex
            const re = new RegExp(/^(?:[a-z0-9+!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i);
            if(!re.test(data.email)) {
                dataElements.email.setError('Please enter a valid email address.', '(e.g.: bob@gmail.com)')
                numErrors++;
            } else {
                dataElements.email.setError(null);
            }
        }

        if (validateItem === 'dateOfBirth' || validateItem === 'all') {
            if(!data.dateOfBirth || !Number.isFinite((new Date(data.dateOfBirth)).getTime()) || !((new Date(data.dateOfBirth)).getTime() >= -2209013725000) || !((new Date(data.dateOfBirth)) < new Date()) || !(((new Date()).getTime()) - (new Date(data.dateOfBirth).getTime()) >= MIN_AGE * 365 * 24 * 60 * 60 * 1000) || !(((new Date()).getTime()) - (new Date(data.dateOfBirth).getTime()) < MAX_AGE * 365 * 24 * 60 * 60 * 1000)) {
                dataElements.dateOfBirth.setError(`Please enter a valid date of birth. (Age ${MIN_AGE} to ${MAX_AGE})`)
                numErrors++;
            } else {
                dataElements.dateOfBirth.setError(null);
                data.dateOfBirth = (new Date(data.dateOfBirth)).getTime() - 60 * 60 * 1000 * 8
            }
        }

        if (validateItem === 'cfmInfoReal' || validateItem === 'all') {
            if(!data.cfmInfoReal) {
                dataElements.cfmInfoReal.setError(true)
                numErrors++;
            } else {
                dataElements.cfmInfoReal.setError(null);
            }
        }

        if (validateItem === 'cfmAcceptTnC' || validateItem === 'all') {
            if(!data.cfmAcceptTnC) {
                dataElements.cfmAcceptTnC.setError(true)
                numErrors++;
            } else {
                dataElements.cfmAcceptTnC.setError(null);
            }
        }

        return { data, numErrors };
    }

    return (
        <>
            {
                isSigningUp && (
                    <FullWidthSpinner
                        text='Signing Up...'
                        heightInVH={isMobile ? 64 : 90}
                        scale={3} />
                )
            }

            <div style={{ display: isSigningUp ? 'none' : '' }}>
                <p className="app-text thin-line">Complete all fields below to enrol your child.</p>
                <VerticalSeparator/>
                {
                    overallErrorText && (
                        <>
                            <HorizontallyCenteringContainer>
                                <div className="input-error">{overallErrorText}</div>
                            </HorizontallyCenteringContainer>
                            <VerticalSeparator/>
                        </>
                    )
                }

                <div>Name (required):</div>
                <HorizontallyCenteringContainer>
                    <input ref={nameEl} className="underline-input" onChange={() => getData({ validateItem: 'name' })}/>
                </HorizontallyCenteringContainer>
                { nameErrorText && <div className="input-error">{nameErrorText}</div> }
                <VerticalSeparator />

                <div>Email (required):</div>
                <HorizontallyCenteringContainer>
                    <input ref={emailEl} className="underline-input" onChange={() => getData({ validateItem: 'email' })}/>
                </HorizontallyCenteringContainer>
                { emailErrorTextLine1 && <div className="input-error">{emailErrorTextLine1}</div> }
                { emailErrorTextLine2 && <div className="input-error">{emailErrorTextLine2}</div> }
                <VerticalSeparator />

                <div>Date of Birth (required):</div>
                <HorizontallyCenteringContainer>
                    <input ref={dateOfBirthEl} type="date" className="underline-input" onChange={() => getData({ validateItem: 'dateOfBirth' })}/>
                </HorizontallyCenteringContainer>
                { dateOfBirthErrorText && <div className="input-error">{dateOfBirthErrorText}</div> }
                <VerticalSeparator />

                <div style={{ fontSize: '0.8em' }}>
                    <div>You may register a child from age {MIN_AGE} to {MAX_AGE} years old.</div>
                    <VerticalSeparator/>
                    <div>Get updated on Cool Rewards Members' privileges and promotions via the following:</div>
                    <VerticalSeparator/>
                    {/* <div className="horizontal-flex">
                        <Form.Check ref={allowSMSCheckbox} className="fixed-size" inline type='checkbox' onChange={() => getData({ validateItem: 'allowSMS' })}/>
                        <div>SMS</div>
                    </div>
                    <VerticalSeparator height='0.25em'/>
                    <div className="horizontal-flex">
                        <Form.Check ref={allowEmailCheckbox} className="fixed-size" inline type='checkbox' onChange={() => getData({ validateItem: 'allowEmail' })}/>
                        <div>Email</div>
                    </div>
                    <VerticalSeparator height='0.25em'/>
                    <div className="horizontal-flex">
                        <Form.Check ref={allowInstantMessengerServicesCheckbox} className="fixed-size" inline type='checkbox' onChange={() => getData({ validateItem: 'allowInstantMessengerServices' })}/>
                        <div>Instant Messenger Services (e.g.: Telegram)</div>
                    </div>
                    <VerticalSeparator height='1em'/> */}
                    <div className="horizontal-flex">
                        <Form.Check ref={cfmInfoRealCheckbox} className="fixed-size" inline type='checkbox' onChange={() => getData({ validateItem: 'cfmInfoReal' })}/>
                        <div>I confirm the above information provided is genuine.</div>
                    </div>
                    { cfmInfoRealErrorText && <div className="input-error">Please confirm that the above information provided is genuine.</div> }
                    <VerticalSeparator height='0.25em'/>
                    <div className="horizontal-flex">
                        <Form.Check ref={cfmAcceptTnCCheckbox} className="fixed-size" inline type='checkbox' onChange={() => getData({ validateItem: 'cfmAcceptTnC' })}/>
                        <div>I agree to the <a href="https://swensens.com.sg/membership-terms-conditions/" target="_blank" rel="noreferrer">Terms & Conditions</a> and <a href="https://swensens.com.sg/privacy-policy/" target="_blank" rel="noreferrer">Privacy Policy</a> of Swensens's Cool Rewards Membership Program</div>
                    </div>
                    { cfmAcceptTnCErrorText && <div className="input-error">Please review the policies and accept them to continue.</div> }
                </div>


                <VerticalSeparator/>
                <HorizontallyCenteringContainer>
                    <Button
                        className="w80 swensens-button"
                        onClick={() => {
                            const { data, numErrors } = getData({ validateItem: 'all' });
                            if (numErrors === 0) {
                                setOverallErrorText(null)
                                console.log('Submitting:', data)
                                setIsSigningUp(true);
                                
                                const dataCopy = Object.assign({}, data);
                                delete dataCopy.allowEmail;
                                delete dataCopy.allowSMS;
                                delete dataCopy.allowInstantMessengerServices;
                                const registrationHash = objectHash(dataCopy);

                                registerChild(data)
                                    .then(({ data: { success, message } }) => {
                                        if (success) {
                                            localStorage.setItem('last-successful-child-registration', JSON.stringify({
                                                registrationHash, data
                                            }))
                                            updateMemberData();
                                            closeModal();
                                        } else {
                                            let lastSuccessfulChildRegistration;
                                            try {
                                                lastSuccessfulChildRegistration = JSON.parse(localStorage.getItem('last-successful-child-registration') || 'null')
                                            } catch {
                                                lastSuccessfulChildRegistration = null;
                                            }
                                            if (!!lastSuccessfulChildRegistration && lastSuccessfulChildRegistration.registrationHash === registrationHash) {
                                                updateMemberData();
                                                closeModal();
                                            } else {
                                                setOverallErrorText(`Error Registering Child. (${message})`);
                                            }
                                        }
                                    })
                                    .catch(e => {
                                        setIsSigningUp(false);
                                        setOverallErrorText(`Error Registering Child. Try again later.`);
                                    })
                                    .then(() => {
                                        setIsSigningUp(false);
                                    })
                            }
                        }}
                    >Register Child</Button>
                </HorizontallyCenteringContainer>
                <HorizontallyCenteringContainer>
                    { overallErrorText && <div className="input-error">{overallErrorText}</div> }
                </HorizontallyCenteringContainer>
            </div>
            <VerticalSeparator height='1.5em'/>
        </>
    );
}
